// Reports shell (R1-R5 + equipment one-pager). Tabs carry their own data loading through the
// shared useReport hook; money tabs (costs, parts) only appear with canViewCosts — the server
// enforces the same right. Every table offers CSV export (client-side, the data is already
// here). Shared mini-primitives (Kpi, BarList, csvDownload) live here to keep the tab files
// under the 500-LOC rule.
import React, { useState, useEffect } from 'react';
import { ArrowLeft, BarChart3, Download } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { Card } from '../ui';
import { CostsTab, HoursTab, PartsTab } from './FinanceTabs';
import { BreakdownsTab, ComplianceTab } from './ReliabilityTabs';
import EquipmentTab from './EquipmentTab';

/** Data-loading ladder shared by every tab: null=loading, error string, or data. */
export function useReport(fetcher, deps) {
  const [state, setState] = useState({ data: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let alive = true;
    setState({ data: null, error: null });
    fetcher()
      .then((data) => { if (alive) setState({ data, error: null }); })
      .catch((e) => { if (alive) setState({ data: null, error: e.message }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadKey]);
  return { ...state, retry: () => setReloadKey((k) => k + 1) };
}

/** KPI tile — label above, big value below. */
export function Kpi({ label, value, tone }) {
  const { isDarkMode } = useTheme();
  const tones = {
    good: isDarkMode ? 'text-emerald-300' : 'text-emerald-700',
    bad: isDarkMode ? 'text-rose-300' : 'text-rose-600',
    warn: isDarkMode ? 'text-amber-300' : 'text-amber-700',
  };
  return (
    <Card className="flex-1 min-w-[130px]">
      <p className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{label}</p>
      <p className={`text-xl font-bold mt-1 ${tones[tone] || (isDarkMode ? 'text-white' : 'text-slate-900')}`}>{value}</p>
    </Card>
  );
}

/** Horizontal bar list — label, value, bar scaled to the max. */
export function BarList({ rows, valueLabel }) {
  const { isDarkMode } = useTheme();
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i}>
          <div className="flex justify-between text-sm">
            <span className={`truncate mr-2 ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>{r.label}</span>
            <span className={`whitespace-nowrap ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
              {r.display != null ? r.display : r.value}{valueLabel ? ` ${valueLabel}` : ''}
            </span>
          </div>
          <div className={`h-1.5 rounded-full mt-1 ${isDarkMode ? 'bg-white/[0.06]' : 'bg-slate-100'}`}>
            <div className={`h-1.5 rounded-full ${r.value < 0 ? 'bg-rose-400' : 'bg-amber-400'}`}
              style={{ width: `${Math.min(100, (Math.abs(r.value) / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** CSV download from column definitions [{key, label}] + row objects. */
export function csvDownload(filename, columns, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map((c) => esc(c.label)).join(';')];
  for (const r of rows) lines.push(columns.map((c) => esc(r[c.key])).join(';'));
  const blob = new Blob([`﻿${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Small export affordance used by the tabs. */
export function ExportButton({ onClick }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  return (
    <button type="button" onClick={onClick}
      className={`flex items-center gap-1 text-xs font-medium ${isDarkMode ? 'text-white/50 hover:text-white' : 'text-slate-400 hover:text-slate-700'}`}>
      <Download size={14} /> {t('pm.rep.exportCsv')}
    </button>
  );
}

export default function ReportsScreen({ me, user, onBack, onOpenOrder }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const canMoney = me.profile.functionRights.canViewCosts;
  const tabs = [
    ...(canMoney ? [{ id: 'costs', label: t('pm.rep.tabCosts') }] : []),
    { id: 'breakdowns', label: t('pm.rep.tabBreakdowns') },
    { id: 'compliance', label: t('pm.rep.tabCompliance') },
    { id: 'hours', label: t('pm.rep.tabHours') },
    ...(canMoney ? [{ id: 'parts', label: t('pm.rep.tabParts') }] : []),
    { id: 'equipment', label: t('pm.rep.tabEquipment') },
  ];
  const [tab, setTab] = useState(tabs[0].id);

  return (
    <div>
      <button type="button" onClick={onBack} className={`flex items-center gap-1 mb-3 text-sm font-medium ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}>
        <ArrowLeft size={16} /> {t('pm.common.back')}
      </button>
      <h2 className={`flex items-center gap-2 text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
        <BarChart3 size={20} /> {t('pm.rep.title')}
      </h2>
      <div className="flex gap-2 mb-4 flex-wrap">
        {tabs.map((x) => (
          <button key={x.id} type="button" onClick={() => setTab(x.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${tab === x.id
              ? (isDarkMode ? 'bg-amber-500/20 text-amber-200 border border-amber-400/40' : 'bg-amber-100 text-amber-800 border border-amber-200')
              : (isDarkMode ? 'bg-white/[0.06] text-white/60' : 'bg-slate-100 text-slate-500')}`}>
            {x.label}
          </button>
        ))}
      </div>
      {tab === 'costs' && <CostsTab me={me} user={user} />}
      {tab === 'breakdowns' && <BreakdownsTab me={me} user={user} />}
      {tab === 'compliance' && <ComplianceTab me={me} user={user} />}
      {tab === 'hours' && <HoursTab me={me} user={user} />}
      {tab === 'parts' && <PartsTab me={me} user={user} />}
      {tab === 'equipment' && <EquipmentTab me={me} user={user} onOpenOrder={onOpenOrder} />}
    </div>
  );
}
